import { useState } from "react";
import { Modal, Platform, Pressable, View } from "react-native";
import { Calendar, type LucideIcon } from "lucide-react-native";
import DateTimePicker, {
  type DateTimePickerEvent,
} from "@react-native-community/datetimepicker";
import {
  isoToDisplay,
  placeholderFor,
  useDateFormat,
} from "@src/packages/utils/useDateFormat";
import { colors } from "../theme/tokens";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { DesktopMaskedDateInput } from "./DesktopMaskedDateInput";
import { MoonText } from "./MoonText";
import { useInputFontSize } from "../utils/inputFontSize";

/**
 * Typed date field. Platform-aware, four-way split:
 *
 *   - **iOS / Android native**: Pressable trigger shows the formatted date
 *     (or the format placeholder when empty); tap opens the OS-native
 *     date picker via `@react-native-community/datetimepicker`. iOS uses
 *     a modal spinner, Android the native modal dialog.
 *   - **Mobile web (Safari iOS, Chrome Android)**: same Pressable trigger
 *     visual; a transparent `<input type="date">` is overlaid so taps
 *     route to the browser's native date picker (iOS Safari spinner,
 *     Android Chrome wheel). The visible text stays under our control so
 *     the empty placeholder respects `dateFormat`.
 *   - **Desktop web**: typeable text input with auto-inserted separators
 *     per `dateFormat`. Keyboard entry beats clicks on desktop.
 *
 * `value` is the canonical "YYYY-MM-DD" string on every surface.
 */
type Props = {
  label: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  /** Supporting copy rendered directly below the label and above the input. */
  labelHint?: string;
  /** "YYYY-MM-DD" or "" for empty. */
  value: string;
  onChange: (next: string) => void;
  /** Placeholder override. Defaults to the user's `dateFormat` pattern
   *  ("YYYY-MM-DD", "MM/DD/YYYY", "DD/MM/YYYY"). */
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
};

function isoToDate(iso: string): Date {
  if (!iso) return new Date();
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime()) ? new Date() : d;
}

function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function MoonDateField({
  label,
  labelIcon,
  labelHint,
  value,
  onChange,
  placeholder,
  error,
  hint,
  required,
  disabled,
  loading,
  testID,
}: Props) {
  const isWeb = Platform.OS === "web";

  // All web surfaces (desktop and narrow browser windows) use the
  // typeable input. The previous `width < 768` branch routed to a
  // `<input type="date">` overlay that triggered the browser's native
  // date popover — confusing on a constrained desktop window where the
  // user expected to just type the date. Real iOS / Android (where the
  // OS picker actually shines) goes through `NativeTriggerDate`
  // regardless of viewport.
  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      {isWeb ? (
        <DesktopMaskedDateInput
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          hasError={!!error}
        />
      ) : (
        <NativeTriggerDate
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          hasError={!!error}
          disabled={disabled}
        />
      )}
    </MoonFormField>
  );
}

/**
 * Shared trigger row used by mobile/native variants. Shows Calendar
 * icon + formatted date or placeholder text. Stays presentational.
 */
function DateTriggerRow({
  displayText,
  isEmpty,
  fontSize,
}: {
  displayText: string;
  isEmpty: boolean;
  fontSize: number;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 10,
      }}
    >
      <Calendar size={13} color={colors.textLight} strokeWidth={1.5} />
      <MoonText
        style={{
          flex: 1,
          fontSize,
          color: isEmpty ? colors.textFaint : colors.text,
          marginLeft: 10,
          lineHeight: 20,
        }}
      >
        {displayText}
      </MoonText>
    </View>
  );
}

function NativeTriggerDate({
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hasError: boolean;
  disabled?: boolean;
}) {
  const { dateFormat } = useDateFormat();
  const fontSize = useInputFontSize();
  const capturePress = usePressCapture("MoonDateField");
  const [open, setOpen] = useState(false);
  const placeholderText = placeholder ?? placeholderFor(dateFormat);
  const displayText = value ? isoToDisplay(value, dateFormat) : placeholderText;
  const isEmpty = !value;

  return (
    <MoonFieldInput hasError={hasError} paddingY={2}>
      <Pressable
        onPress={() => {
          if (disabled) return;
          capturePress(pressLabel(displayText), "open_date_picker");
          setOpen(true);
        }}
        disabled={disabled}
      >
        <DateTriggerRow
          displayText={displayText}
          isEmpty={isEmpty}
          fontSize={fontSize}
        />
      </Pressable>
      {open && Platform.OS === "ios" ? (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={() => setOpen(false)}
        >
          <Pressable
            onPress={() => setOpen(false)}
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.4)",
              justifyContent: "flex-end",
            }}
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                alignItems: "center",
                backgroundColor: "#FFFFFF",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                paddingTop: 8,
                paddingBottom: 24,
              }}
            >
              <DateTimePicker
                value={isoToDate(value)}
                mode="date"
                display="spinner"
                onChange={(_: DateTimePickerEvent, selected?: Date) => {
                  if (selected) onChange(dateToIso(selected));
                }}
              />
              <Pressable
                onPress={() => {
                  capturePress(pressLabel("Done"), "confirm_date");
                  setOpen(false);
                }}
                style={{
                  alignSelf: "center",
                  marginTop: 4,
                  paddingHorizontal: 24,
                  paddingVertical: 10,
                }}
              >
                <MoonText
                  style={{
                    fontSize: 16,
                    fontWeight: "600",
                    color: colors.warm,
                  }}
                >
                  Done
                </MoonText>
              </Pressable>
            </Pressable>
          </Pressable>
        </Modal>
      ) : null}
      {open && Platform.OS === "android" ? (
        <DateTimePicker
          value={isoToDate(value)}
          mode="date"
          display="default"
          onChange={(event: DateTimePickerEvent, selected?: Date) => {
            setOpen(false);
            if (event.type === "set" && selected) {
              onChange(dateToIso(selected));
            }
          }}
        />
      ) : null}
    </MoonFieldInput>
  );
}
